import React from 'react';

/* SAP S/4HANA Cloud-style primitives used across Finance / HR / Teams pages. */

// Object-page header: breadcrumb + title + KPI chip row
export function ObjectPageHeader({ breadcrumb = [], title, subtitle, kpis = [], actions }) {
  return (
    <div className="border-b border-border bg-card" data-testid="object-header">
      <div className="px-6 pt-4 pb-3">
        <nav className="text-xs text-muted-foreground flex items-center gap-1" data-testid="breadcrumb">
          {breadcrumb.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="mx-1">/</span>}
              <span className={i === breadcrumb.length - 1 ? 'text-foreground font-medium' : ''}>{b}</span>
            </React.Fragment>
          ))}
        </nav>
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </div>
      {kpis.length > 0 && (
        <div className="px-6 pb-4 flex items-center gap-6 flex-wrap" data-testid="kpi-row">
          {kpis.map((k, i) => (
            <div key={i} className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold" style={{ color: k.color || '#002677' }}>{k.value}</span>
              <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{k.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Horizontal tab strip — SAP Fiori "section anchor" style with underline
export function SectionTabs({ tabs, active, onChange }) {
  return (
    <div className="px-6 border-b border-border bg-card">
      <div className="flex gap-6 -mb-px overflow-x-auto" data-testid="section-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            data-testid={`tab-${t.key}`}
            className={`py-3 text-sm whitespace-nowrap border-b-[3px] transition-colors ${
              active === t.key
                ? 'border-[#002677] text-foreground font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}{typeof t.count === 'number' && <span className="ml-1.5 text-xs text-muted-foreground">({t.count})</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Compact table with colored left-border status indicator (SAP "object list item")
export function FioriTable({ columns, rows, emptyLabel = 'No items', rowStatusColor }) {
  return (
    <div className="bg-card border border-border overflow-x-auto" data-testid="fiori-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/40 border-b border-border">
            {columns.map((c) => (
              <th
                key={c.key}
                className="text-left text-[11px] font-semibold tracking-[0.14em] uppercase text-muted-foreground px-4 py-2.5"
                style={c.width ? { width: c.width } : undefined}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-10 text-sm text-muted-foreground">{emptyLabel}</td></tr>
          ) : rows.map((r, i) => (
            <tr
              key={r.id || i}
              className="border-b border-border hover:bg-muted/20 transition-colors"
              style={rowStatusColor ? { borderLeft: `3px solid ${rowStatusColor(r)}` } : undefined}
              data-testid={`row-${r.id || i}`}
            >
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-2.5 align-middle">
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Pantone Fiori status palette
export const STATUS_COLOR = {
  draft:       '#8A8E99',
  pending:     '#FFC72C',
  sent:        '#41B6E6',
  approved:    '#00B140',
  paid:        '#00B140',
  rejected:    '#E4002B',
  overdue:     '#E4002B',
  cancelled:   '#8A8E99',
  reimbursed:  '#00945D',
  processed:   '#002677',
  confirmed:   '#002677',
  shipped:     '#41B6E6',
  delivered:   '#00B140',
  active:      '#00B140',
  on_leave:    '#FFC72C',
  terminated:  '#8A8E99',
};

export function StatusPill({ status }) {
  const color = STATUS_COLOR[status] || '#8A8E99';
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium capitalize"
      style={{ color }}
      data-testid={`status-${status}`}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

// Small currency formatter
export const fmtCurrency = (v, cur = 'EUR') => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: cur, maximumFractionDigits: 0,
}).format(v || 0);
